import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { DbUser } from "../models";
import { BaseDbObject } from "../models/db";
import { Customer } from "./reports";
import {
  fetchApiDelete,
  fetchApiGet,
  fetchApiPost,
  fetchApiPut,
} from "./utils";

export interface AssignableUser {
  id: string, name: string
}

export const TransferKnowledgeOwnership = z.object({
  id: z.string().uuid(),
  owner_id: z.string().uuid()
})

export const ArchiveKnowledgeAssignmentValidation = z.object({
  id: z.string().uuid(),
  assignmentId: z.string().uuid(),
});

export const CreateCommonBlockValidation = z.object({
  content: z.string().min(1),
  special_permission_id: z.string().uuid().optional(),
});

export const UpdateCommonBlockValidation = z.object({
  id: z.string().uuid(),
  content: z.string().min(1),
  special_permission_id: z.string().uuid().optional(),
});

export const UpdateKnowledgeItemAssignmentStepValidation = z.object({
  knowledge_item_id: z.string().uuid(),
  assignment_id: z.string().uuid(),
  step_id: z.string().uuid(),

  status: z.boolean()
});

export const AddTagValidation = z.object({
  name: z.string().min(1),
  special_permission_id: z.string().uuid().optional(),
});

export const UpdateTagValidation = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  special_permission_id: z.string().uuid().optional(),
});

export const CreateKnowledgeItemValidation = z.object({
  title: z.string().min(1),
  description: z.string().default(""),
  content: z.string().optional(),
  special_permission_id: z.string().uuid().optional(),
  owner_id: z.string().uuid(),
  main_category_id: z.string().uuid().optional(),
  secondary_category_id: z.string().uuid().optional(),
  item_type: z.enum(['task', 'common-block']),
});

export const UpdateKnowledgeItemValidation = z.object({
  id: z.string().uuid(),
  title: z.string().min(1),
  description: z.string().min(1),
  last_audit_date: z.string().optional(),
  content: z.string().min(1),
  special_permission_id: z.string().uuid().optional(),
  tags: z.array(z.string().uuid()).optional(),
  main_category_id: z.string().uuid().optional().nullable(),
  secondary_category_id: z.string().uuid().optional().nullable(),
  steps: z.array(z.object({
    id: z.string().uuid().optional(),
    name: z.string().min(1),
    description: z.string().default(""),
    blocking: z.boolean().optional().default(true),
    completion_by: z.enum(['assignee', 'trainer', 'any']).optional().default('trainer'),
  })).optional(),
});

export const UploadFileValidation = z.object({
  mimetype: z.string(),
  file_name: z.string(),
  file_size: z.number(),
  knowledge_item_id: z.string().uuid(),
});

export const SearchKnowledgeItemsValidation = z.object({
  query: z.string().min(1, "Search query is required"),
  fields: z.array(z.enum(["title"])).min(1).default(["title"]),
  page: z.number().min(1).default(1),
  pageSize: z.number().min(1).max(50).default(15),
});

export const AddAssignmentValidation = z.object({
  knowledge_item_id: z.string().uuid(),
  assignee_id: z.string().uuid(),
  trainer_id: z.string().uuid().optional(),
  backup_trainer_id: z.string().uuid().optional(),
  status: z.enum(['IN_PROGRESS']),
});

export const UpdateAssignmentValidation = z.object({
  knowledge_item_id: z.string().uuid(),
  assignment_id: z.string().uuid(),
  status: z.enum(['COMPLETED', 'IN_PROGRESS']),
});

export interface KnowledgeItem extends BaseDbObject {
  title: string;
  description: string;
}

interface KnowledgeItemsParams {
  page: number;
  filter?: 'all' | 'owned' | 'assigned' | 'training' | 'common-blocks';
  activeTagId?: string;
  emptyTags?: boolean;
  last_audit_date?: { before: Date | string }
  main_category_id?: string | null;
  secondary_category_id?: string | null;
}

function assembleKnowledgeItemsQueryParams(params: KnowledgeItemsParams) {
  const queryParams = new URLSearchParams();
  queryParams.append("page", `${params.page}`);
  queryParams.append("pageSize", `21`);
  if (params.filter) {
    queryParams.append("filter", params.filter);
  }
  if (params.activeTagId) {
    queryParams.append("tag_id", params.activeTagId);
  }
  if (params.emptyTags) {
    queryParams.append("empty_tags", `${params.emptyTags}`);
  }
  if (params.last_audit_date?.before) {
    queryParams.append("last_audit_date", JSON.stringify(params.last_audit_date));
  }

  if (params.main_category_id) {
    queryParams.append("main_category_id", params.main_category_id);
  }

  if (params.secondary_category_id) {
    queryParams.append("secondary_category_id", params.secondary_category_id);
  }
  return queryParams;
}

export function fetchKnowledgeItemsPage(params: KnowledgeItemsParams) {
  const queryParams = assembleKnowledgeItemsQueryParams(params);

  return fetchApiGet<{
    data: KnowledgeItem[];
    pagination: {
      totalItems: number;
      totalPages: number;
      pageSize: number;
      currentPage: number;
    }
  }>(`knowledge/items`, queryParams);
}

export interface ListOwnerAssignments {
  id: string;
  knowledge_item_id: string;
  assignee_id: string;
  trainer_id: string;
  assigned_at: string;
  status: string;

  Assignee: { name: string };
  KnowledgeItem: { title: string; steps: { order: number; }[]; };
  active_step: { title: string; order: number; };
}

export interface InProgressAssignmentStep {
  step_id: string;
  title: string;
  description: string;
  order: number;
  is_blocker: boolean;
  completion_by: string;
  is_completed: boolean;
  is_active: boolean;
  completed_at: Date | null;
  completed_by: { id: string; name: string; email: string } | null;
  acknowledged_by: { id: string; name: string; email: string } | null;
}

export interface InProgressAssignment {
  assignment_id: string;
  knowledge_item: {
    id: string;
    title: string;
    description: string;
  };
  assignee: { id: string; name: string; email: string };
  trainer: { id: string; name: string; email: string };
  backup_trainer: { id: string; name: string; email: string } | null;
  assigned_at: Date;
  status: "IN_PROGRESS";
  steps_completed: number;
  total_steps: number;
  all_steps: InProgressAssignmentStep[];
  active_step: {
    step_id: string;
    title: string;
    order: number;
  } | null;
}

export function useListOwnersAssignedUsersTasks() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["knowledge", "owner_assigned", "tasks"],
    queryFn: () =>
      fetchApiGet<ListOwnerAssignments[]>("knowledge/owner/assigned", queryParams),
    retry: 2,
    keepPreviousData: true,
  });
}

export function useGetInProgressReport() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["knowledge", "assignments", "in-progress"],
    queryFn: () =>
      fetchApiGet<InProgressAssignment[]>("knowledge/assignments/report/in-progress", queryParams),
    retry: 2,
    keepPreviousData: true,
  });
}

export function useKnowledgeItemsPaginated({ params, queryFn, enabled, keyOverride }: {
  params: KnowledgeItemsParams;
  queryFn: any;
  enabled?: boolean;
  keyOverride?: string;
}) {
  const middleKey = keyOverride ?? "tasks";
  return useQuery({
    queryKey: ["knowledge", middleKey, assembleKnowledgeItemsQueryParams(params).toString()],
    queryFn: () => queryFn(params),
    retry: 1,
    keepPreviousData: true,
    enabled: enabled ?? true,
  });
}

export function useGetKnowledgeItem(taskId?: string) {
  return useQuery({
    queryKey: ["knowledge", "task", taskId],
    queryFn: () => fetchApiGet<KnowledgeApi.KnowledgeItem>(`knowledge/items/${taskId}`),
    enabled: !!taskId,
    retry: 1,
  });
}

export function useGetAssignableUsers() {
  return useQuery({
    queryKey: ["knowledge", "assignableUsers"],
    queryFn: () => fetchApiGet<AssignableUser[]>(`knowledge/assignable-users`),
  });
}

export function useGetKnowledgePermissions() {
  return useQuery({
    queryKey: ["knowledge", "permissions"],
    queryFn: () => fetchApiGet<{ id: string, name: string; description: string }[]>(`knowledge/permissions`),
  });
}

export function useCreateKnowledgeReport() {
  const queryClient = useQueryClient();

  return async () => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<{ link: string }>(
      `knowledge/items/report`,
      {},
      queryParams
    );

    return result;
  };

}

export function useCreateKnowledgeItem() {
  const queryClient = useQueryClient();

  return async (params: typeof CreateKnowledgeItemValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<any>(
      `knowledge/items`,
      params,
      queryParams
    );
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["knowledge", "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge", "all-tasks"] });
    }

    return result;
  };
}

export function useArchiveKnowledgeItemAsignment() {
  const queryClient = useQueryClient();

  return async (params: typeof ArchiveKnowledgeAssignmentValidation._type) => {
    const result = await fetchApiDelete<any>(
      `knowledge/items/${params.id}/assignments/${params.assignmentId}`,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["knowledge"] });
    }

    return result;
  };
}


export function useDeleteTask() {
  const queryClient = useQueryClient();

  return async (params: { id: string }) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiDelete<any>(
      `knowledge/items/${params.id}`,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["knowledge", "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge", "all-tasks"] });
    }

    return result;
  };
}

export function useUpdateKnowledgeItem() {
  const queryClient = useQueryClient();

  return async (params: typeof UpdateKnowledgeItemValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPut<any>(
      `knowledge/items/${params.id}`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["knowledge", "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge", "all-tasks"] });
    }

    return result;
  };
}

export function useAddAssignment() {
  const queryClient = useQueryClient();

  return async (params: typeof AddAssignmentValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<any>(
      `knowledge/items/${params.knowledge_item_id}/assignments`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["knowledge", "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge", "all-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge", "task", params.knowledge_item_id] });
    }

    return result;
  };
}

export function useUpdateAssignment() {
  const queryClient = useQueryClient();

  return async (params: typeof UpdateAssignmentValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPut<any>(
      `knowledge/items/${params.knowledge_item_id}/assignments/${params.assignment_id}`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["knowledge", "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge", "all-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge", "task", params.knowledge_item_id] });
    }

    return result;
  };
}

export function useCreateKnowledgeUploadLink() {
  const queryClient = useQueryClient();

  return async (params: typeof UploadFileValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<any>(
      `knowledge/file/upload/link`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["knowledge", "task", params.knowledge_item_id] });
    }

    return result;
  };
}

export function useGenerateGetPresignedUrl() {
  const queryClient = useQueryClient();

  return async (attachmentId: string) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<any>(
      `knowledge/file/${attachmentId}`,
      {},
      queryParams
    );

    return result;
  };
}

export function useCreateTag() {
  const queryClient = useQueryClient();

  return async (params: typeof AddTagValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<any>(
      `knowledge/tags`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["knowledge", "tags"] });
    }

    return result;
  };
}

export function useUpdateTag() {
  const queryClient = useQueryClient();

  return async (params: typeof UpdateTagValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPut<any>(
      `knowledge/tags/${params.id}`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["knowledge"] });
    }

    return result;
  };
}

export function useUpdateAssignmentStepCompletion() {
  const queryClient = useQueryClient();

  return async (params: typeof UpdateKnowledgeItemAssignmentStepValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPut<any>(
      `knowledge/items/${params.knowledge_item_id}/assignments/${params.assignment_id}/steps/${params.step_id}`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["knowledge", "task", params.knowledge_item_id] });
    }

    return result;
  }
}

export function useCreateCommonBlock() {
  const queryClient = useQueryClient();

  return async (params: typeof CreateCommonBlockValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<any>(
      `knowledge/common-blocks`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["knowledge", "common-blocks"] });
    }

    return result;
  }
}

export function useUpdateCommonBlock() {
  const queryClient = useQueryClient();

  return async (params: typeof UpdateCommonBlockValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPut<any>(
      `knowledge/common-blocks/${params.id}`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["knowledge", "common-blocks"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge", "common-block", params.id] });
    }

    return result;
  }
}

export function useKnowledgeItemTransferOwnership() {
  const queryClient = useQueryClient();

  return async (params: typeof TransferKnowledgeOwnership._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPut<any>(
      `knowledge/items/${params.id}/transfer`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["knowledge"] });
    }

    return result;
  }
}

export interface TagsBreakdown {
  tags: { id: string; name: string; }[];
  main_category_ids: string[];
  main_to_secondary_id_breakdown: Record<string, string[]>
}

export function useGetCommonBlocks() {
  return useQuery({
    queryKey: ["knowledge", "common-blocks"],
    queryFn: () => fetchApiGet<KnowledgeApi.KnowledgeCommonBlock[]>(`knowledge/common-blocks`),
  });
}

export function useGetCommonBlock(id: string) {
  return useQuery({
    queryKey: ["knowledge", "common-block", id],
    queryFn: () => fetchApiGet<KnowledgeApi.KnowledgeCommonBlock>(`knowledge/common-blocks/${id}`),
    enabled: !!id,
  });
}

export function useGetKnowledgeTags() {
  return useQuery({
    queryKey: ["knowledge", "tags"],
    queryFn: () => fetchApiGet<{ id: string, name: string }[]>(`knowledge/tags`),
  });
}

export function useGetKnowledgeTagsBreakdown() {
  return useQuery({
    queryKey: ["knowledge", "tags", "breakdown"],
    queryFn: () => fetchApiGet<TagsBreakdown>(`knowledge/tags/breakdown`),
  });
}

export function useSearchKnowledgeItems({
  query,
  page = 1,
  pageSize = 9,
  enabled = true,
}: {
  query: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["knowledge", "search", query, page, pageSize],
    queryFn: () =>
      fetchApiPost<{
        data: KnowledgeApi.KnowledgeItem[];
        pagination: {
          currentPage: number;
          totalPages: number;
          totalItems: number;
          pageSize: number;
        };
      }>(
        "knowledge/items/search",
        {
          query,
          fields: ["title"],
          page,
          pageSize,
        },
        new URLSearchParams()
      ),
    enabled: enabled && query.length > 0,
    retry: 1,
    keepPreviousData: true,
  });
}



export namespace KnowledgeApi {
  export type StepCompletionBy = 'assignee' | 'trainer' | 'any';
  export type KnowledgeAttachmentType = 'FILE' | 'LINK' | 'IMAGE' | 'VIDEO' | 'AUDIO' | 'PDF';
  export type KnowledgeAssignmentStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';

  export interface KnowledgeTaskTag extends BaseDbObject {
    name: string;
    special_permission_id?: string;

    SpecialPermission?: SpecialPermission;
  }

  export interface KnowledgeCommonBlock extends BaseDbObject {
    content: string;
    special_permission_id?: string;

    SpecialPermission?: SpecialPermission;
  }

  export interface KnowledgeTagLink extends BaseDbObject {
    tag_id: string;
    item_id?: string;
    common_block_id?: string;

    KnowledgeTaskTag?: KnowledgeTaskTag;
    KnowledgeItem?: KnowledgeItem;
    KnowledgeCommonBlock?: KnowledgeCommonBlock;
  }

  export interface KnowledgeItem extends BaseDbObject {
    item_type: 'task' | 'common-block';
    title: string;
    description: string;
    content: string;

    owner_id: string;
    special_permission_id?: string;

    last_audit_date?: string | Date;
    requires_review?: boolean;

    steps?: KnowledgeStep[];
    attachments?: KnowledgeAttachment[];

    assignments?: KnowledgeAssignment[];
    assignmentsCount?: number;

    Owner?: DbUser;
    SpecialPermission?: SpecialPermission;
  }

  export interface KnowledgeStep extends BaseDbObject {
    knowledge_item_id: string;

    title: string;
    description: string;
    order: number | null;
    is_blocker: boolean;
    completion_by: StepCompletionBy;
  }

  export interface KnowledgeAttachment extends BaseDbObject {
    name: string;
    type: KnowledgeAttachmentType;
    url: string;
    position?: number;
  }

  export interface KnowledgeAssignment extends BaseDbObject {
    knowledge_item_id: string;

    assignee_id: string;
    trainer_id?: string;
    backup_trainer_id?: string;

    assigned_at: Date;
    completed_at?: Date;

    status: KnowledgeAssignmentStatus;


    KnowledgeItem?: KnowledgeItem;
    Assignee?: DbUser;
    Trainer?: DbUser;
    BackupTrainer?: DbUser;

    StepCompletions?: StepCompletion[];
  }

  export interface StepCompletion extends BaseDbObject {
    step_id: string;

    assignment_id: string;

    completed_by_id: string;
    acknowledged_by_id?: string;
    last_action_id?: string;

    completed_at: Date;
  }

  export interface SpecialPermission extends BaseDbObject {
    name: string;
    description: string;
  }

  export interface UserSpecialPermission extends BaseDbObject {
    user_id: string;
    permission_id: string;
  }
}
